import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { ConfirmDialog } from '@repo/shared-client/components/blocks'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from '@web/src/lib/intl/react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type { DeleteEventMutation, DeleteEventMutationVariables } from 'types/graphql'

export const DELETE_EVENT: TypedDocumentNode<DeleteEventMutation, DeleteEventMutationVariables> = gql`
  mutation DeleteEvent($eventIdOrSlug: String!) {
    deleteEvent(eventIdOrSlug: $eventIdOrSlug) {
      id
    }
  }
`

type CustomModalProps = {
  open: boolean
  onOpenChange?: (details: { open: boolean }) => void
}

const EventDelete = ({
  event,
  ...props
}: CustomModalProps & {
  event: {
    id: string
    name: string
    slug?: string
  }
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showSpinner, setShowSpinnerValue] = useState(false)

  const [deleteEvent] = useMutation(DELETE_EVENT, {
    update: (cache, { data }) => {
      const deletedEventId = data?.deleteEvent?.id ?? event.id
      const cacheId = cache.identify({ __typename: 'Event', id: deletedEventId })

      if (cacheId) {
        cache.evict({ id: cacheId })
        cache.gc()
      }
    },
    onCompleted: () => {
      closeDialog()
      navigate({ to: '/event/list' })
      toast.success(t('Event Deleted'))
    },
    onError: (error) => {
      toast.error(error.message)
      closeDialog()
    },
  })

  const onSubmit = () => {
    setShowSpinnerValue(true)
    deleteEvent({
      variables: { eventIdOrSlug: event.slug || event.id },
    })
  }

  const closeDialog = () => {
    props?.onOpenChange?.({ open: false })
  }

  return (
    <ConfirmDialog
      confirmationPlaceholder={t('Type the event name here to confirm')}
      confirmationPrompt={t('Please enter the event name to confirm')}
      confirmationText={event.name}
      loading={showSpinner}
      onConfirm={onSubmit}
      onOpenChange={(details) => {
        props?.onOpenChange?.(details)

        if (!details.open) {
          setShowSpinnerValue(false)
        }
      }}
      open={props.open}
      text={t('Deleting this event will remove all reviews and media with it.')}
      title={t('Delete Event')}
      variant="delete"
    />
  )
}

export default EventDelete
